import React from 'react';
import { Landmark, X } from 'lucide-react';

const stripNonDigit = (s, max) => s.replace(/\D/g, '').slice(0, max);

/**
 * "Add Bank Account" modal. Fully controlled — the parent owns the
 * `newAccount` object and submit handler.
 */
export default function AddBankAccountModal({
  newAccount,
  setNewAccount,
  onClose,
  onSubmit,
}) {
  const update = (field, value) => setNewAccount({ ...newAccount, [field]: value });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Landmark className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Add Bank Account</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div>
            <label htmlFor="bank-name-input" className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
            <input
              id="bank-name-input"
              type="text"
              value={newAccount.bank_name}
              onChange={(e) => update('bank_name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Chase, Bank of America"
              required
              data-testid="bank-name-input"
            />
          </div>

          <div>
            <label htmlFor="account-holder-input" className="block text-sm font-medium text-gray-700 mb-2">
              Account Holder Name *
            </label>
            <input
              id="account-holder-input"
              type="text"
              value={newAccount.account_holder_name}
              onChange={(e) => update('account_holder_name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              placeholder="Name on the account"
              required
              data-testid="account-holder-input"
            />
          </div>

          <div>
            <label htmlFor="account-type-select" className="block text-sm font-medium text-gray-700 mb-2">Account Type *</label>
            <select
              id="account-type-select"
              value={newAccount.account_type}
              onChange={(e) => update('account_type', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              data-testid="account-type-select"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="routing-number-input" className="block text-sm font-medium text-gray-700 mb-2">Routing Number *</label>
              <input
                id="routing-number-input"
                type="text"
                value={newAccount.routing_number}
                onChange={(e) => update('routing_number', stripNonDigit(e.target.value, 9))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="9 digits"
                maxLength={9}
                required
                data-testid="routing-number-input"
              />
            </div>
            <div>
              <label htmlFor="account-number-input" className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
              <input
                id="account-number-input"
                type="text"
                value={newAccount.account_number}
                onChange={(e) => update('account_number', stripNonDigit(e.target.value, 17))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="Account number"
                required
                data-testid="account-number-input"
              />
            </div>
          </div>

          {/* Optional Card Linkage */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Card Linkage (Optional)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="card-type-select" className="block text-sm text-gray-600 mb-2">Card Type</label>
                <select
                  id="card-type-select"
                  value={newAccount.card_type}
                  onChange={(e) => update('card_type', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  data-testid="card-type-select"
                >
                  <option value="">None</option>
                  <option value="visa">Visa</option>
                  <option value="mastercard">Mastercard</option>
                </select>
              </div>
              <div>
                <label htmlFor="card-last-four-input" className="block text-sm text-gray-600 mb-2">Last 4 Digits</label>
                <input
                  id="card-last-four-input"
                  type="text"
                  value={newAccount.card_last_four}
                  onChange={(e) => update('card_last_four', stripNonDigit(e.target.value, 4))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="1234"
                  maxLength={4}
                  disabled={!newAccount.card_type}
                  data-testid="card-last-four-input"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="is_primary"
              checked={newAccount.is_primary}
              onChange={(e) => update('is_primary', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_primary" className="text-sm text-gray-700">
              Set as primary account for transfers
            </label>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
            data-testid="save-account-btn"
          >
            Add Bank Account
          </button>
        </form>
      </div>
    </div>
  );
}
